import { Request, Response } from 'express';

import createAttributesPart from '@/utils/links/createAttributesPart';
import createCategoriesPart from '@/utils/links/createCategoriesPart';
import { createQueryPart } from '@/utils/links/helpers';
import locationData from '@/server/constants/locationData';

const generateBreadcrumbListItem = ({ name, item, position }) => ({
  '@type': 'ListItem',
  position: Number(position),
  name,
  item,
});

const capitalizeFirstLetter = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const maxAttributesValuesNumber = 2;

const getBreadcrumbs = (req: Request, res: Response, sellerCanonicalUrl?: string): TMetaBreadcrumbs => {
  const {
    l1Category,
    l2Categories,
    l2Category,
    location,
    attributesValuesKeys,
    attributesValuesIds,
    searchQuery,
    year,
    pagination,
    seller,
    i18n: { t },
  } = res.locals;
  const { hostname } = req;
  const tenantName = t('breadcrumbs.platform');
  const baseUrl = `https://${hostname}`;
  let categoriesPart = '';
  let queryPart = '';

  const itemListElement = [
    generateBreadcrumbListItem({
      name: tenantName,
      position: 1,
      item: `${baseUrl}/`,
    }),
  ];

  if (l1Category) {
    const { name } = l1Category;
    categoriesPart = createCategoriesPart({ l1Category });
    const l1Item = generateBreadcrumbListItem({
      name,
      position: itemListElement.length + 1,
      item: `${baseUrl}${categoriesPart}/`,
    });
    itemListElement.push(l1Item);
  }

  if (location) {
    const cityEntry = locationData[location];
    if (cityEntry) {
      const cityDisplayName = cityEntry.name;
      const categoryName = l1Category?.name || '';
      const locationItem = generateBreadcrumbListItem({
        name: `${categoryName} in ${cityDisplayName}`,
        position: itemListElement.length + 1,
        item: `${baseUrl}${categoriesPart}/h/${location}/`,
      });
      itemListElement.push(locationItem);
    }
  }

  if (l2Categories && l2Categories.length > 0) {
    const l2Names = l2Categories.map((cat: TCategory) => cat.name).join(', ');
    categoriesPart = createCategoriesPart({ l1Category, l2Categories });
    const l2Item = generateBreadcrumbListItem({
      name: l2Names,
      position: itemListElement.length + 1,
      item: `${baseUrl}${categoriesPart}/`,
    });
    itemListElement.push(l2Item);
  }

  if (searchQuery) {
    queryPart = createQueryPart(searchQuery);
    const l2Item = generateBreadcrumbListItem({
      name: searchQuery,
      position: itemListElement.length + 1,
      item: `${baseUrl}${categoriesPart}${queryPart}/`,
    });
    itemListElement.push(l2Item);
  }

  if (attributesValuesKeys) {
    const slicedAttributesValuesKeys = attributesValuesKeys.slice(0, maxAttributesValuesNumber);
    const attributesName = slicedAttributesValuesKeys
      .map((x: string) => capitalizeFirstLetter(x.replace(/-/g, ' ')))
      .join(' - ');

    const attributes: { attributeValueLabel: string; attributeValueId: string }[] = [];
    slicedAttributesValuesKeys.forEach((attrValue: string, i: number) => {
      attributes.push({
        attributeValueLabel: attrValue,
        attributeValueId: attributesValuesIds[i],
      });
    });

    const attributesPart = createAttributesPart(attributes, false);

    const category = l2Categories?.[0] || l1Category;
    const categoryKey = category ? category.name : '';
    const name = `${searchQuery || categoryKey} - ${attributesName}`;

    const attributesItem = generateBreadcrumbListItem({
      name,
      position: itemListElement.length + 1,
      item: `${baseUrl}${categoriesPart}${queryPart}${attributesPart}/`,
    });
    itemListElement.push(attributesItem);
  }

  if (sellerCanonicalUrl && seller) {
    const hasFilters =
      l1Category ||
      l2Category ||
      (l2Categories && l2Categories.length > 0) ||
      location ||
      (Array.isArray(attributesValuesKeys) && attributesValuesKeys.length > 0) ||
      (Array.isArray(attributesValuesIds) && attributesValuesIds.length > 0) ||
      year ||
      (pagination && pagination.offset > 0);

    if (!hasFilters) {
      const sellerName: string = seller.title || seller.name || '';
      if (sellerName) {
        itemListElement.length = 1; // keep only homepage; discard any query/attribute items
        itemListElement.push(
          generateBreadcrumbListItem({
            name: sellerName,
            position: 2,
            item: sellerCanonicalUrl,
          }),
        );
      }
    }
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
};

export default getBreadcrumbs;
